import { Outlet, RootRoute, Route, redirect } from '@tanstack/react-router';
import { lazy, Suspense } from 'react';
import { useTranslation } from 'react-i18next';

import { AppShell } from '@/components/app-shell.js';
import type { AuthState } from '@/lib/auth-context.js';
import { LoginPage } from '@/pages/login-page.js';
import { NotFoundPage } from '@/pages/not-found-page.js';
import { DashboardPage } from '@/pages/dashboard-page.js';

// Lazy-loaded heavy screens
const AdminCostsPage = lazy(() => import('@/pages/admin-costs-page.js').then(m => ({ default: m.AdminCostsPage })));
const AdminJobRunsPage = lazy(() => import('@/pages/admin-job-runs-page.js').then(m => ({ default: m.AdminJobRunsPage })));
const AdminJobsPage = lazy(() => import('@/pages/admin-jobs-page.js').then(m => ({ default: m.AdminJobsPage })));
const AdminSharedTokensPage = lazy(() => import('@/pages/admin-shared-tokens-page.js').then(m => ({ default: m.AdminSharedTokensPage })));
const AdminProxyPage = lazy(() => import('@/pages/admin-proxy-page.js').then(m => ({ default: m.AdminProxyPage })));
const AdminUsersPage = lazy(() => import('@/pages/admin-users-page.js').then(m => ({ default: m.AdminUsersPage })));
const AiKeysPage = lazy(() => import('@/pages/ai-keys-page.js').then(m => ({ default: m.AiKeysPage })));
const JobEditPage = lazy(() => import('@/pages/job-edit-page.js').then(m => ({ default: m.JobEditPage })));
const JobNewPage = lazy(() => import('@/pages/job-new-page.js').then(m => ({ default: m.JobNewPage })));
const JobsListPage = lazy(() => import('@/pages/jobs-list-page.js').then(m => ({ default: m.JobsListPage })));
const LegacyImportPage = lazy(() => import('@/pages/legacy-import-page.js').then(m => ({ default: m.LegacyImportPage })));
const RunDetailPage = lazy(() => import('@/pages/run-detail-page.js').then(m => ({ default: m.RunDetailPage })));
const SettingsPage = lazy(() => import('@/pages/settings-page.js').then(m => ({ default: m.SettingsPage })));
const TemplateEditPage = lazy(() => import('@/pages/template-edit-page.js').then(m => ({ default: m.TemplateEditPage })));
const TemplatesListPage = lazy(() => import('@/pages/templates-list-page.js').then(m => ({ default: m.TemplatesListPage })));

function PageFallback(): JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="flex min-h-[200px] items-center justify-center text-muted-foreground text-sm">
      {t('common.loading')}
    </div>
  );
}

function WithSuspense({ children }: { children: React.ReactNode }): JSX.Element {
  return <Suspense fallback={<PageFallback />}>{children}</Suspense>;
}

export interface RouterContext {
  auth: AuthState;
}

const rootRoute = new RootRoute({
  component: () => <Outlet />,
  notFoundComponent: NotFoundPage,
});

const loginRoute = new Route({
  getParentRoute: () => rootRoute,
  path: 'login',
  component: LoginPage,
});

const authedRoute = new Route({
  getParentRoute: () => rootRoute,
  id: 'authed',
  beforeLoad: ({ context, location }) => {
    const ctx = context as RouterContext;
    if (!ctx.auth.user) {
      throw redirect({ to: '/login', search: { from: location.pathname } });
    }
  },
  component: () => (
    <AppShell>
      <Outlet />
    </AppShell>
  ),
});

const indexRoute = new Route({
  getParentRoute: () => authedRoute,
  path: '/',
  component: DashboardPage,
});

const jobsLayoutRoute = new Route({
  getParentRoute: () => authedRoute,
  path: 'jobs',
  component: () => <Outlet />,
});

const jobsListRoute = new Route({
  getParentRoute: () => jobsLayoutRoute,
  path: '/',
  component: () => <WithSuspense><JobsListPage /></WithSuspense>,
});

const jobNewRoute = new Route({
  getParentRoute: () => jobsLayoutRoute,
  path: 'new',
  component: () => <WithSuspense><JobNewPage /></WithSuspense>,
});

const jobDetailRoute = new Route({
  getParentRoute: () => jobsLayoutRoute,
  path: '$jobId',
  component: () => <Outlet />,
});

const jobEditRoute = new Route({
  getParentRoute: () => jobDetailRoute,
  path: 'edit',
  component: () => <WithSuspense><JobEditPage /></WithSuspense>,
});

const runDetailRoute = new Route({
  getParentRoute: () => jobDetailRoute,
  path: 'runs/$runId',
  component: () => <WithSuspense><RunDetailPage /></WithSuspense>,
});

const templatesLayoutRoute = new Route({
  getParentRoute: () => authedRoute,
  path: 'templates',
  component: () => <Outlet />,
});

const templatesListRoute = new Route({
  getParentRoute: () => templatesLayoutRoute,
  path: '/',
  component: () => <WithSuspense><TemplatesListPage /></WithSuspense>,
});

const templatesNewRoute = new Route({
  getParentRoute: () => templatesLayoutRoute,
  path: 'new',
  component: () => <WithSuspense><TemplateEditPage /></WithSuspense>,
});

const templateEditRoute = new Route({
  getParentRoute: () => templatesLayoutRoute,
  path: '$templateId',
  component: () => <WithSuspense><TemplateEditPage /></WithSuspense>,
});

const settingsLayoutRoute = new Route({
  getParentRoute: () => authedRoute,
  path: 'settings',
  component: () => <Outlet />,
});

const settingsIndexRoute = new Route({
  getParentRoute: () => settingsLayoutRoute,
  path: '/',
  component: () => <WithSuspense><SettingsPage /></WithSuspense>,
});

const aiKeysRoute = new Route({
  getParentRoute: () => settingsLayoutRoute,
  path: 'ai-keys',
  component: () => <WithSuspense><AiKeysPage /></WithSuspense>,
});

const toolsLayoutRoute = new Route({
  getParentRoute: () => authedRoute,
  path: 'tools',
  component: () => <Outlet />,
});

const importRoute = new Route({
  getParentRoute: () => toolsLayoutRoute,
  path: 'import',
  component: () => <WithSuspense><LegacyImportPage /></WithSuspense>,
});

const adminRoute = new Route({
  getParentRoute: () => authedRoute,
  id: 'admin',
  beforeLoad: ({ context }) => {
    const ctx = context as RouterContext;
    if (ctx.auth.user?.role !== 'ADMIN') {
      throw redirect({ to: '/' });
    }
  },
  component: () => <Outlet />,
});

const adminUsersRoute = new Route({
  getParentRoute: () => adminRoute,
  path: 'admin/users',
  component: () => <WithSuspense><AdminUsersPage /></WithSuspense>,
});

const adminProxyRoute = new Route({
  getParentRoute: () => adminRoute,
  path: 'admin/proxy',
  component: () => <WithSuspense><AdminProxyPage /></WithSuspense>,
});

const adminJobsRoute = new Route({
  getParentRoute: () => adminRoute,
  path: 'admin/jobs',
  component: () => <WithSuspense><AdminJobsPage /></WithSuspense>,
});

const adminJobRunsRoute = new Route({
  getParentRoute: () => adminRoute,
  path: 'admin/jobs/$jobId/runs',
  component: () => <WithSuspense><AdminJobRunsPage /></WithSuspense>,
});

const adminSharedTokensRoute = new Route({
  getParentRoute: () => adminRoute,
  path: 'admin/shared-tokens',
  component: () => <WithSuspense><AdminSharedTokensPage /></WithSuspense>,
});

const adminCostsRoute = new Route({
  getParentRoute: () => adminRoute,
  path: 'admin/costs',
  component: () => <WithSuspense><AdminCostsPage /></WithSuspense>,
});

export const routeTree = rootRoute.addChildren([
  loginRoute,
  authedRoute.addChildren([
    indexRoute,
    jobsLayoutRoute.addChildren([
      jobsListRoute,
      jobNewRoute,
      jobDetailRoute.addChildren([jobEditRoute, runDetailRoute]),
    ]),
    templatesLayoutRoute.addChildren([templatesListRoute, templatesNewRoute, templateEditRoute]),
    settingsLayoutRoute.addChildren([settingsIndexRoute, aiKeysRoute]),
    toolsLayoutRoute.addChildren([importRoute]),
    adminRoute.addChildren([adminUsersRoute, adminProxyRoute, adminJobsRoute, adminJobRunsRoute, adminSharedTokensRoute, adminCostsRoute]),
  ]),
]);
